import React, { useState, memo, useCallback, useRef, useEffect } from 'react';
import { Divider } from '@/components/elements/Divider/Divider';
import type { CTA } from '@/types/Plan';
import { ParsePlanType } from '@/components/elements/ParsePlanType';
import { PlanSummary } from '@/components/elements/PlanSummary';
import { VisitCopays } from '@/components/elements/VisitCopays';
import { MonthlyPremium } from '@/components/elements/MonthlyPremium';
import LinkIconWhite from '@/assets/icons/NewTabIconWhite.svg';
import LinkIcon from '@/assets/icons/NewTabIcon.svg';
import Image from 'next/image';
import { ParseExchange } from '@/components/elements/ParseExchange';
import Button from '@/components/elements/Button/Button';
import Icon from '@/components/elements/Icon/Icon';
import ChevroneTop from '@/assets/icons/chevrone-top.svg';
import type { PlanWithAgePremiums } from '@/components/widgets/PlansSection';

export type PlanCardProps = {
    plan: PlanWithAgePremiums & {
        idStr?: string;
        offExchange?: boolean;
        selfEnroll?: boolean;
        highlighted?: boolean;
        availableNote?: string;
    };
    isStateCountrySearch?: boolean;
    familyAges?: string[];
    cta?: CTA;
    employerContributionAmount?: number | null;
    onToggleCompare?: () => void;
    isCompared?: boolean;
    comparePosition?: number;
    compareTotal?: number;
    
    forceShowDetails?: boolean;
    hideToggleSub?: boolean;
    showCompareButton?: boolean;
    
    hideViewAllRatesButton?: boolean;
    
    defaultExpanded?: boolean;
    
    fullWidth?: boolean;
};

const parsePremiumValue = (premium: string | undefined): string | null => {
    if (!premium) return null;
    const premiumStr = String(premium).trim();
    const cleanedPremium = premiumStr.replace(/[^0-9.]/g, '');
    const numValue = parseFloat(cleanedPremium);
    if (isNaN(numValue)) return null;
    return numValue.toFixed(2);
};

const getDetailsUrl = (
    baseUrl: string | null,
    params?: {
        premium?: string;
        premium18?: string;
        premium65?: string;
    }
): string => {
    if (!baseUrl) return '#';

    let urlString: string;

    try {
        const url = new URL(baseUrl);
        const pathname = url.pathname;

        if (pathname.includes('/details/')) {
            urlString = baseUrl;
        } else {
        const pathParts = pathname.split('/').filter(Boolean);
        if (pathParts.length > 0) {
            const planId = pathParts[pathParts.length - 1];
            url.pathname = `/details/${planId}`;
                urlString = url.toString();
            } else {
                urlString = baseUrl;
            }
        }
    } catch {
        if (baseUrl.includes('/details/')) {
            urlString = baseUrl;
        } else {
        const match = baseUrl.match(/^(https?:\/\/[^/]+)(\/.*)$/);
        if (match) {
            const [, base, path] = match;
            const pathParts = path.split('/').filter(Boolean);
            if (pathParts.length > 0) {
                const planId = pathParts[pathParts.length - 1];
                    urlString = `${base}/details/${planId}`;
                } else {
                    urlString = baseUrl;
                }
            } else {
                urlString = baseUrl;
            }
        }
    }

    // Add query parameters if provided
    if (params) {
        try {
            const url = new URL(urlString);
            if (params.premium) {
                url.searchParams.set('premium', params.premium);
            }
            if (params.premium18) {
                url.searchParams.set('premium18', params.premium18);
            }
            if (params.premium65) {
                url.searchParams.set('premium65', params.premium65);
            }
            return url.toString();
        } catch {
            // If URL parsing fails, append params manually
            const separator = urlString.includes('?') ? '&' : '?';
            const queryParams: string[] = [];
            if (params.premium) {
                queryParams.push(`premium=${encodeURIComponent(params.premium)}`);
            }
            if (params.premium18) {
                queryParams.push(`premium18=${encodeURIComponent(params.premium18)}`);
            }
            if (params.premium65) {
                queryParams.push(`premium65=${encodeURIComponent(params.premium65)}`);
            }
            return queryParams.length > 0 ? `${urlString}${separator}${queryParams.join('&')}` : urlString;
        }
    }

    return urlString;
};

export const PlanCard: React.FC<PlanCardProps> = memo(({
    plan,
    isStateCountrySearch = false,
    familyAges = [],
    employerContributionAmount = null,
    onToggleCompare,
    isCompared = false,
    comparePosition,
    compareTotal = 3,
    forceShowDetails = false,
    hideToggleSub = false,
    hideViewAllRatesButton = false,
    defaultExpanded = false,
    fullWidth = false,
}) => {
    const [toggleSub, onToggleSub] = useState(!!defaultExpanded);
    const [isHovered, setIsHovered] = useState(false);
    const compareButtonRef = useRef<HTMLButtonElement>(null);
    
    const handleToggleSub = useCallback(() => {
        onToggleSub((prev) => !prev);
    }, []);
    
    // Defensive: prevent any parent click handlers / links / forms from hijacking the click.
    // This avoids "page reload" / navigation when toggling compare.
    const handleCompareClick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleCompare?.();
        },
        [onToggleCompare],
    );
    
    // Update button text directly via DOM when hover state changes
    useEffect(() => {
        const button = compareButtonRef.current;
        if (!button || !isCompared) return;
        
        if (isHovered) {
            button.textContent = 'Remove';
        } else {
            button.textContent = `Comparing (${comparePosition || 1}/${compareTotal})`;
        }
    }, [isHovered, isCompared, comparePosition, compareTotal]);
    const borderColor = 'border-[#0077DF]';
    const ringColor = plan.highlighted ? 'shadow-[0_0_0_3px_rgba(59,130,246,0.08)]' : 'shadow-sm';
    const expanded = forceShowDetails || toggleSub;
    const widthClass = fullWidth ? 'max-w-none' : 'max-w-[909px]';

    return (
        <article className={`rounded-2xl ${ringColor} border bg-white ${borderColor} w-full ${widthClass} px-[16px]`}>
            <div className="flex flex-row items-center justify-between gap-3 py-2">
                <div className="w-full min-w-0">
                    <div className="flex w-full flex-row items-center gap-3">
                        {plan.carrier.logo_url && (
                            <img
                                src={plan.carrier.logo_url}
                                alt="logo"
                                className="size-24 shrink-0 rounded object-contain"
                                loading="lazy"
                                decoding="async"
                            />
                        )}
                        <div className="hidden sm:flex flex-col gap-1 w-full min-w-0">
                            <p className="w-full max-w-[450px] text-sm font-medium whitespace-normal text-gray-800 sm:text-base">
                                {plan.display_name}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex shrink-0 flex-col items-end gap-2">
                    <ParsePlanType type={plan.level} />
                    <ParseExchange on_market={plan.on_market} off_market={plan.off_market} />
                </div>
            </div>

            <div className="bg-blue-50 -mx-[16px] px-[16px] pb-6 lg:pb-3 pt-[10px] rounded-b-[16px]">
                <PlanSummary
                    plan={plan}
                    isSub={expanded}
                    toggleSub={handleToggleSub}
                    familyAges={familyAges}
                    hideDetailsToggle={forceShowDetails}
                />
                <Divider className="my-1 sm:my-1" />
                {expanded && (
                    <>
                        <VisitCopays plan={plan} />
                        <Divider className="my-1 sm:my-1" />
                        <div className="w-full md:col-span-3">
                            {(() => {
                                const links = [
                                    plan.benefits_summary_url && { url: plan.benefits_summary_url, text: 'Summary of Benefits (SBC)' },
                                    plan.network_provider_directory_url && { url: plan.network_provider_directory_url, text: 'Find In-Network Providers' },
                                    plan.drug_formulary_url && { url: plan.drug_formulary_url, text: 'Rx Drug Formulary' },
                                ].filter(Boolean) as Array<{ url: string; text: string }>;

                                const linkCount = links.length;
                                const isTwoLinks = linkCount === 2;

                                return (
                                    <div className={`mt-[7px] flex w-full ${isTwoLinks ? 'flex-row flex-wrap justify-start sm:justify-start' : 'flex-col'} items-start gap-[7px] ${isTwoLinks ? 'sm:flex-row md:flex-col lg:flex-row sm:flex-wrap md:flex-nowrap lg:flex-wrap sm:items-center md:items-start lg:items-center sm:gap-x-[7px] md:gap-[7px] lg:gap-x-[7px] sm:gap-y-[7px] lg:gap-y-[7px]' : 'sm:flex-row md:flex-col lg:flex-row sm:flex-wrap md:flex-nowrap lg:flex-wrap sm:items-center md:items-start lg:items-center sm:justify-between md:justify-start lg:justify-between sm:gap-x-[7px] md:gap-[7px] lg:gap-x-[7px] sm:gap-y-[7px] lg:gap-y-[7px]'}`}>
                                        {plan.benefits_summary_url && (
                                            <a
                                                className={`flex ${isTwoLinks ? 'w-auto' : 'w-full'} shrink-0 gap-1 text-base text-primary hover:underline sm:w-auto sm:text-base`}
                                                href={plan.benefits_summary_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Summary of Benefits (SBC) <Image src={LinkIcon} alt="Link" width={16} height={16} />
                                            </a>
                                        )}
                                        {plan.network_provider_directory_url && (
                                            <a
                                                className={`flex ${isTwoLinks ? 'w-auto' : 'w-full'} shrink-0 gap-1 text-base text-primary hover:underline sm:w-auto sm:text-base`}
                                                href={plan.network_provider_directory_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Find In-Network Providers <Image src={LinkIcon} alt="Link" width={16} height={16} />
                                            </a>
                                        )}
                                        {plan.drug_formulary_url && (
                                            <a
                                                className={`flex ${isTwoLinks ? 'w-auto' : 'w-full'} shrink-0 gap-1 text-base text-primary hover:underline sm:w-auto sm:text-base`}
                                                href={plan.drug_formulary_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Rx Drug Formulary <Image src={LinkIcon} alt="Link" width={16} height={16} />
                                            </a>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                        <Divider className="my-[8px] sm:my-[8px]" />
                    </>

                )}

                {!hideToggleSub && (
                <div className="flex w-full flex-col items-center justify-center gap-1 lg:hidden">
                    <div
                        className="flex w-full cursor-pointer justify-center gap-1 text-base whitespace-nowrap text-primary hover:underline"
                        onClick={handleToggleSub}
                    >
                        {expanded ? 'Hide Details' : 'Show Details'}{' '}
                        <Icon
                            src={ChevroneTop}
                            className={`${expanded ? `rotate-0` : 'rotate-180'} transition-all duration-200`}
                        />
                    </div>
                </div>
                )}
                {!hideToggleSub && <Divider className="my-[8px] sm:my-[8px] lg:hidden" />}

                <MonthlyPremium
                    plan={plan}
                    isStateCountrySearch={isStateCountrySearch}
                    premiumAge18={plan.premiumAge18}
                    premiumAge65={plan.premiumAge65}
                    familyAges={familyAges}
                    employerContributionAmount={employerContributionAmount}
                    hideViewAllRatesButton={hideViewAllRatesButton}
                    allRatesModalCompare={
                        onToggleCompare
                            ? {
                                  onToggleCompare,
                                  isCompared,
                                  comparePosition,
                                  compareTotal,
                              }
                            : undefined
                    }
                    rightSection={
                        <div className="flex w-full flex-col gap-2 lg:flex-row lg:items-center lg:w-auto">
                            {onToggleCompare && (
                                <div className="flex w-full lg:flex-none lg:w-auto">
                                    {isCompared ? (
                                        <button
                                            ref={compareButtonRef}
                                            type="button"
                                            className="btn btn-primary btn-outline w-full h-[43px] text-base font-normal text-center sm:text-base lg:flex-none lg:w-[162px]"
                                            onClick={handleCompareClick}
                                            onMouseEnter={() => setIsHovered(true)}
                                            onMouseLeave={() => setIsHovered(false)}
                                        >
                                            {`Comparing (${comparePosition || 1}/${compareTotal})`}
                                        </button>
                                    ) : (
                                        <Button
                                            type="button"
                                            variant="primary"
                                            outline
                                            className="w-full h-[43px] text-base font-normal text-center sm:text-base lg:flex-none lg:w-[162px]"
                                            onClick={handleCompareClick}
                                        >
                                            Add to compare
                                        </Button>
                                    )}
                                </div>
                            )}
                        <Button
                            onClick={() => {
                                if (plan.predictable_benefits_plan_url) {
                                        // Determine which premium parameters to include
                                        let premiumParams: {
                                            premium?: string;
                                            premium18?: string;
                                            premium65?: string;
                                        } | undefined;

                                        if (isStateCountrySearch && (plan.premiumAge18 || plan.premiumAge65)) {
                                            // For state/county search with age-specific premiums
                                            const premium18 = parsePremiumValue(plan.premiumAge18);
                                            const premium65 = parsePremiumValue(plan.premiumAge65);
                                            if (premium18 || premium65) {
                                                premiumParams = {};
                                                if (premium18) premiumParams.premium18 = premium18;
                                                if (premium65) premiumParams.premium65 = premium65;
                                            }
                                        } else {
                                            // For regular search (zip/age) - use plan.premium
                                            const premium = parsePremiumValue(plan.premium);
                                            if (premium) {
                                                premiumParams = { premium };
                                            }
                                        }

                                        const urlString = getDetailsUrl(plan.predictable_benefits_plan_url, premiumParams);
                                    window.open(urlString, '_blank', 'noopener,noreferrer');
                                }
                            }}
                            variant="primary"
                            className="w-full h-[43px] text-sm sm:text-center sm:text-base lg:flex-none lg:w-auto"
                            disabled={!plan.predictable_benefits_plan_url}
                        >
                            Plan Details
                            <Image src={LinkIconWhite} alt="Link" width={16} height={16} />
                        </Button>
                        </div>
                    }
                />
            </div>
        </article>
    );
}, (prevProps, nextProps) => {
    // Custom comparison function for better performance
    return (
        prevProps.plan.id === nextProps.plan.id &&
        prevProps.isStateCountrySearch === nextProps.isStateCountrySearch &&
        prevProps.familyAges?.length === nextProps.familyAges?.length &&
        JSON.stringify(prevProps.familyAges) === JSON.stringify(nextProps.familyAges) &&
        prevProps.plan.premiumAge18 === nextProps.plan.premiumAge18 &&
        prevProps.plan.premiumAge65 === nextProps.plan.premiumAge65 &&
        prevProps.employerContributionAmount === nextProps.employerContributionAmount &&
        prevProps.isCompared === nextProps.isCompared &&
        prevProps.comparePosition === nextProps.comparePosition &&
        prevProps.compareTotal === nextProps.compareTotal &&
        prevProps.forceShowDetails === nextProps.forceShowDetails &&
        prevProps.hideToggleSub === nextProps.hideToggleSub &&
        prevProps.hideViewAllRatesButton === nextProps.hideViewAllRatesButton &&
        prevProps.defaultExpanded === nextProps.defaultExpanded &&
        prevProps.fullWidth === nextProps.fullWidth
    );
});
